import { useEffect, useState, useContext } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import SessionContext from '../context/SessionProvider';
import NoAccessView from '../app/views/noAccessView/NoAccessView';
import PackagesView from '../app/views/subscription/packagesView';
import { Spinner } from '@/components/ui/Spinner';
import {
  getCurrentUserInfo,
  getUsersGroups,
  getWorkspaceUsers,
} from '../apiServices/userApi';
import { setAllWorkspaces, updateWorkspaceUsers } from '../app/features/workspace/workspaceSlice';
import { setDetails, setOldUserDetails } from '../app/features/user/userSlice';
import type { RootState } from '@/store/index';

interface AuthenticatedRouteProps {
  children: React.ReactNode;
}

export default function AuthenticatedRoute({ children }: AuthenticatedRouteProps) {
  const [loading, setLoading] = useState(true);
  const { createdGroup, allWorkspaces, currentUserGroup, rootGroup } = useSelector(
    (state: RootState) => state.workspace,
  );
  const { uid } = useSelector((state: RootState) => state.user);
  const dispatch = useDispatch();
  const location = useLocation();
  const session = useContext(SessionContext);
  const isAuthenticated = session.isAuthenticated;

  useEffect(() => {
    const fetchAppData = async () => {
      setLoading(true);
      const myselfRes = await getCurrentUserInfo(currentUserGroup);
      const groups = await getUsersGroups();

      const myself = myselfRes.response.Item;
      const packages = myselfRes.response.Packages;
      const selectedPackage = myselfRes.response.Package;

      dispatch(setAllWorkspaces({ items: myself.groups, uid, packages, selectedPackage }));
      dispatch(setDetails(myself));
      dispatch(setOldUserDetails(myself));

      const allUsersRes = await getWorkspaceUsers();
      dispatch(updateWorkspaceUsers(allUsersRes.response));
      setLoading(false);
    };
    if (uid) {
      fetchAppData();
    }
  }, [uid, currentUserGroup]); // eslint-disable-line react-hooks/exhaustive-deps

  if (location.pathname.includes('/in/link') && !isAuthenticated && !uid) {
    return <Navigate to={location.pathname.replace('/in', '')} replace />;
  }

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  let activeGroupIsRoot = false;
  if (allWorkspaces) {
    const activeWorkspace = allWorkspaces.find((group) => currentUserGroup === group.id);
    if (activeWorkspace) {
      activeGroupIsRoot = activeWorkspace.parent === 'root';
    }
  }

  if (isAuthenticated) {
    const isInAccountSettings = location.pathname.includes('/in/account');
    if (rootGroup?.status === '3' || rootGroup?.status === '4') {
      return <PackagesView />;
    }
    if (!activeGroupIsRoot) {
      return children;
    } else if (activeGroupIsRoot && isInAccountSettings) {
      return children;
    } else if (activeGroupIsRoot) {
      return <Navigate to="/in/account/workspace" replace />;
    } else {
      return <NoAccessView />;
    }
  } else {
    return (
      <Navigate
        to={`/login?redirect=${location.pathname}${location.search}`}
        replace
      />
    );
  }
}
